import trim from 'lodash/trim';
import { Component, OnChanges, SimpleChanges, Input, ElementRef, Output, AfterContentInit } from '@angular/core';
import { ngHelper, CurrencyFormatPipe, IGiftCard } from '@cakeiteasy/web-utils';
import { TranslateService } from '@ngx-translate/core';

import { CartServiceV2 } from '../../../cart/services/cart-v2.service';
import { ICart, IAppliedDiscount } from '../../../cart/models/cart/i-cart';
import { CART_PROMO_CODE_TYPES, GIFT_CARD_ERROR_CODE, PROMO_CODE_ERROR_CODES } from '../../constants/common';
import { GiftCardsManagerService } from '../../../api-services/managers/gift-cards-manager.service';
import { EventEmitter } from '@angular/core';
import { DISCOUNT_TYPES } from '../../../cart';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'cart-promo-code-n-gift-card',
  templateUrl: './cart-promo-code-n-gift-card.component.html',
  styleUrls: ['./cart-promo-code-n-gift-card.component.scss']
})
export class CartPromoCodeNGiftCardComponent implements OnChanges, AfterContentInit {
  @Input() cart: ICart;
  @Input() currency: string;
  @Input() applyDiscountFor: CART_PROMO_CODE_TYPES;
  @Input() giftCardEnabled: boolean;
  @Input() giftCard: IGiftCard;
  @Input() firstStringStyle: string;
  @Input() discountAddedStyles: string;
  @Input() errorMsg = '';

  @Output() onGiftCardEmit = new EventEmitter<IGiftCard>();
  @Output() onPromoCodeApplied = new EventEmitter<CART_PROMO_CODE_TYPES>();

  public newDiscountCode: string;
  public isPromoCollapsed: boolean;
  public appliedDiscount: IAppliedDiscount = null;
  public giftCardMsg = '';
  public busy: Promise<any>;

  public get isApplyDiscountEnabled() {
    return (!this.giftCard || !this.giftCard.isApplied) && (
      !this.appliedDiscount || this.appliedDiscount.type !== DISCOUNT_TYPES.BAKERY_DISCOUNT
      && this.appliedDiscount.type !== DISCOUNT_TYPES.PROMO_CODE
    );
  }

  constructor(private _cartService: CartServiceV2,
              private _giftCardManager: GiftCardsManagerService,
              private _elRef: ElementRef,
              private _translate: TranslateService,
              private _currencyFormat: CurrencyFormatPipe) {
  }

  ngAfterContentInit() {
    this._initLinks();
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'cart', (cart: ICart) => {
      if (!cart) {
        return;
      }

      this.appliedDiscount = cart.appliedDiscount || null;
      this.isPromoCollapsed = !this.appliedDiscount
        || this.appliedDiscount.type !== DISCOUNT_TYPES.BAKERY_DISCOUNT && this.appliedDiscount.type !== DISCOUNT_TYPES.PROMO_CODE;
    });

    ngHelper.ifChanged(changes, 'giftCard', giftCard => {
      if (giftCard && giftCard.isApplied) {
        this._setGiftCardMsg();
      } else {
        this.cancel();
      }
    });

    ngHelper.ifChanged(changes, 'errorMsg', (errorMsg: string) => {
      if (!errorMsg) {
        return;
      }

      this.isPromoCollapsed = false;
    });
  }

  public cancel() {
    this.newDiscountCode = null;
    this.errorMsg = '';
    this.isPromoCollapsed = true;
  }

  public codeChanged() {
    this.errorMsg = '';
  }

  public async applyDiscount(code) {
    code = trim(code);
    this.errorMsg = '';
    const { STORE, BAKERY, GIFT_CARD } = CART_PROMO_CODE_TYPES;

    try {
      if (this.applyDiscountFor === STORE) {
        await (this.busy = this._cartService.applyDiscount(code, this.cart.bakery.country.code));
        this.onPromoCodeApplied.emit(STORE);
      } else if (this.applyDiscountFor === BAKERY) {
        await (this.busy = this._cartService.applyBakeryDiscount(code));
        this.onPromoCodeApplied.emit(BAKERY);
      } else if (this.applyDiscountFor === GIFT_CARD) {
        return this.busy = this._tryGiftCard(code);
      }
    } catch (e) {
      if (!e.error) {
        console.error(e);
        return;
      }

      this.errorMsg = this.getPromoCodeErrorMessage(e.error);
    }
  }

  private getPromoCodeErrorMessage({ code, validFromDate, validThroughDate }: {
    code: PROMO_CODE_ERROR_CODES,
    validFromDate?: string,
    validThroughDate: string
  }): string {
    const { DISCOUNT_INVALID_DATE } = PROMO_CODE_ERROR_CODES;
    const msgPrefix = 'orderProcess.payment.promoCodeSection.errors.';

    if (code === DISCOUNT_INVALID_DATE) {
      return this._translate.instant(msgPrefix + DISCOUNT_INVALID_DATE, {
        validFromDate: moment(validFromDate, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        validThroughDate: moment(validThroughDate, 'YYYY-MM-DD').format('DD.MM.YYYY'),
      });
    }

    return this._translate.instant(msgPrefix + code)
      || this._translate.instant('orderProcess.payment.promoCodeSection.error');
  }

  private _tryGiftCard(code: string) {
    return this._giftCardManager.getGiftCardByCode(code)
      .then((giftCard: IGiftCard) => {
        this.onGiftCardEmit.emit(giftCard);
        this.giftCard = giftCard;
        this._setGiftCardMsg();
        this.newDiscountCode = null;
      })
      .catch((res) => {
        const code = res.error && res.error.code;
        switch (code) {
          case GIFT_CARD_ERROR_CODE.EXPIRED:
            return this.errorMsg = this._translate
              .instant('orderProcess.payment.promoCodeSection.errors.giftCardIsExpired');
          case GIFT_CARD_ERROR_CODE.EMPTY:
            return this.errorMsg = this._translate
              .instant('orderProcess.payment.promoCodeSection.errors.giftCardIsEmpty');
          default:
            this.errorMsg = this._translate
              .instant('orderProcess.payment.promoCodeSection.errors.giftCardNotFound');
        }
      });
  }

  private _setPromoCodeType(type: CART_PROMO_CODE_TYPES) {
    this.applyDiscountFor = type;
    this.isPromoCollapsed = false;
  }

  private _setGiftCardMsg() {
    const currency = this.cart.bakery.country.currencySymbol;
    const invoiceFee = this.cart.invoiceFee || 0;
    const reserved = this.giftCard.valueRemains;
    const totalPrice = this.cart.totalPrice;
    const totalWithoutFee = totalPrice - invoiceFee;

    if (totalWithoutFee <= reserved) {
      this.giftCardMsg = reserved - totalWithoutFee === 0
        ? this._translate.instant('orderProcess.payment.promoCodeSection.payFullOrderWithoutRemainder', {
          sum: `-${this._currencyFormat.transform(totalWithoutFee, currency)}`
        })
        : this._translate.instant('orderProcess.payment.promoCodeSection.payFullOrderWithRemainder', {
          sum: `-${this._currencyFormat.transform(totalWithoutFee, currency)}`,
          remainder: `${this._currencyFormat.transform(reserved - totalWithoutFee, currency)}`
        });
    } else {
      this.giftCardMsg = this._translate.instant('orderProcess.payment.promoCodeSection.payWithGiftCardContinuePayment', {
        sum: `${this._currencyFormat.transform(reserved, currency)}`,
        remainder: `${this._currencyFormat.transform(totalPrice - reserved, currency)}`,
      });
    }
  }

  private _initLinks() {
    setTimeout(() => {
      const giftLink = this._elRef.nativeElement.querySelector('a[href="#gift-card"]');
      const promoLink = this._elRef.nativeElement.querySelector('a[href="#promo-code"]');
      const clickHere = this._elRef.nativeElement.querySelector('a[href="#click-here"]');

      if (giftLink && promoLink) {
        giftLink.addEventListener('click', ($event) => {
          $event.preventDefault();
          this._setPromoCodeType(CART_PROMO_CODE_TYPES.GIFT_CARD);
          this.newDiscountCode = this._giftCardManager.giftCardCode;
        });

        promoLink.addEventListener('click', ($event) => {
          $event.preventDefault();
          this._setPromoCodeType(CART_PROMO_CODE_TYPES.STORE);
          this.newDiscountCode = null;
          this.errorMsg = '';
        });
      }

      if (clickHere) {
        clickHere.addEventListener('click', ($event) => {
          $event.preventDefault();
          this.isPromoCollapsed = false;
          this.newDiscountCode = null;
          this.errorMsg = '';
        });
      }
    }, 50);
  }
}
